import {
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  type FeedbackStatus,
  type FeedbackType,
  PLATFORM_SUBSCRIPTION_SORT_FIELDS,
  PLATFORM_TENANT_SORT_FIELDS,
  SUBSCRIPTION_STATUSES,
  type SubscriptionStatus,
  TENANT_STATUSES,
  type TenantStatus,
} from '@sahab/contracts'
import { z } from 'zod'

/**
 * The console's sign-in page: where to go afterwards (checked again before use), and why a cloudTech Hub
 * sign-in came back (`sso_error`, set by the API; an unknown value reads as a failure).
 */
export const platformLoginSearchSchema = z.object({
  redirect: z.string().max(2000).optional().catch(undefined),
  sso_error: z.string().max(100).optional().catch(undefined),
})

type TenantSortField = (typeof PLATFORM_TENANT_SORT_FIELDS)[number]
export type TenantSort = TenantSortField | `-${TenantSortField}`

export const TENANT_PAGE_SIZES = [25, 50, 100] as const

export function isTenantSort(value: string): value is TenantSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (PLATFORM_TENANT_SORT_FIELDS as readonly string[]).includes(field)
}

/** The tenants list lives in the URL; an invalid value falls back to the default. */
export const tenantListSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof TENANT_PAGE_SIZES)[number] =>
      (TENANT_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
  sort: z.string().refine(isTenantSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  status: z.enum(TENANT_STATUSES).optional().catch(undefined),
})
export type TenantListSearch = z.infer<typeof tenantListSearchSchema>

export interface TenantListParams {
  page: number
  pageSize: number
  sort: TenantSort | undefined
  q: string | undefined
  status: TenantStatus | undefined
}

export function toTenantListQuery(search: TenantListSearch): TenantListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? TENANT_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    status: search.status,
  }
}

export const TENANT_TABS = ['profile', 'modules', 'users', 'subscriptions', 'residency', 'activity'] as const
export type TenantTab = (typeof TENANT_TABS)[number]

/** The tenant page's open tab (Profile, the default, is never written to the URL). */
export const tenantDetailSearchSchema = z.object({
  tab: z.enum(TENANT_TABS).optional().catch(undefined),
})

const pageSizeSchema = z
  .number()
  .refine((n): n is (typeof TENANT_PAGE_SIZES)[number] =>
    (TENANT_PAGE_SIZES as readonly number[]).includes(n),
  )
  .optional()
  .catch(undefined)

// ── subscriptions ────────────────────────────────────────────────────────────

type SubscriptionSortField = (typeof PLATFORM_SUBSCRIPTION_SORT_FIELDS)[number]
export type SubscriptionSort = SubscriptionSortField | `-${SubscriptionSortField}`

export function isSubscriptionSort(value: string): value is SubscriptionSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (PLATFORM_SUBSCRIPTION_SORT_FIELDS as readonly string[]).includes(field)
}

/** "Ending soon": active subscriptions ending within this many days. */
export const ENDING_SOON_DAYS = 30

export const subscriptionSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: pageSizeSchema,
  sort: z.string().refine(isSubscriptionSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  status: z.enum(SUBSCRIPTION_STATUSES).optional().catch(undefined),
  ending: z.boolean().optional().catch(undefined),
})
export type SubscriptionSearch = z.infer<typeof subscriptionSearchSchema>

export interface SubscriptionListParams {
  page: number
  pageSize: number
  sort: SubscriptionSort | undefined
  q: string | undefined
  status: SubscriptionStatus | undefined
  tenantId: string | undefined
  endingWithin: number | undefined
}

export function toSubscriptionListQuery(
  search: SubscriptionSearch,
  tenantId?: string,
): SubscriptionListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? TENANT_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    status: search.status,
    tenantId,
    endingWithin: search.ending ? ENDING_SOON_DAYS : undefined,
  }
}

// ── feedback ─────────────────────────────────────────────────────────────────

export const feedbackSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: pageSizeSchema,
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  status: z.enum(FEEDBACK_STATUSES).optional().catch(undefined),
  /** The feedback type (not `type`: another list's search already uses that name). */
  feedbackType: z.enum(FEEDBACK_TYPES).optional().catch(undefined),
})
export type FeedbackSearch = z.infer<typeof feedbackSearchSchema>

export interface FeedbackListParams {
  page: number
  pageSize: number
  q: string | undefined
  status: FeedbackStatus | undefined
  type: FeedbackType | undefined
}

export function toFeedbackListQuery(search: FeedbackSearch): FeedbackListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? TENANT_PAGE_SIZES[0],
    q: search.q || undefined,
    status: search.status,
    type: search.feedbackType,
  }
}
